/* istanbul ignore file */
import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';

import { getSessionStorage } from '@bingads-webui/storage';
import { useAdsStudioServices, getTaskPoller } from '../services';
import { FolderAssetType, checkVideosIntermediateStatus } from '../model';

// use session storeage to sync the in progress tasks count between
// asset-library pages and summary bar

const KEY = 'ASSET_LIBRARY_STORAGE';

const updateInProgressTasksStatus = (inProgressTaskCount) => {
  const ss = getSessionStorage();
  if (ss.supportsStorage()) {
    if (inProgressTaskCount > 0) {
      ss.set(KEY, { inProgressTaskCount });
    } else {
      ss.invalidate(KEY);
    }
  }
};

export const VideoTaskStatusPoller = ({ interval }) => {
  const {
    getVideoTaskStatus,
    assets,
  } = useAdsStudioServices();

  const newVideoTasks = useRef({});
  const isPollerRunning = useRef(false);

  const currentTaskPoller = useRef(null);

  const startVideoTaskStatusPoller = useCallback(() => {
    const taskPoller = getTaskPoller({
      task: () => {
        isPollerRunning.current = true;

        const queryPromise = _.isEmpty(newVideoTasks.current)
          ? Promise.resolve({ totalCount: 0 })
          : getVideoTaskStatus(newVideoTasks.current);

        return queryPromise.then((res) => {
          const { totalCount } = res || {};

          updateInProgressTasksStatus(totalCount || 0);

          const isFinished = totalCount === 0;

          if (isFinished) {
            isPollerRunning.current = false;
          }

          return { isFinished };
        });
      },
      interval,
    });
    taskPoller.start();
    const stopPoller = () => {
      taskPoller.stop();
      isPollerRunning.current = false;
    };
    return { stopPoller };
  }, [getVideoTaskStatus, interval]);

  useEffect(() => {
    newVideoTasks.current = assets ? assets.reduce((prev, asset) => {
      if (asset.folderAssetType === FolderAssetType.Video && checkVideosIntermediateStatus(asset)) {
        return { ...prev, [asset.id]: asset.status };
      }
      return prev;
    }, {}) : {};

    // start pollerif there are no running poller
    if (!isPollerRunning.current && !_.isEmpty(newVideoTasks.current)) {
      const { stopPoller } = startVideoTaskStatusPoller();
      currentTaskPoller.current = stopPoller;
    }

    // stop poller when component unmount.
    return () => {
      if (currentTaskPoller.current && isPollerRunning.current) {
        currentTaskPoller.current();
      }
    };
  }, [assets, startVideoTaskStatusPoller]);

  return null;
};

VideoTaskStatusPoller.propTypes = {
  interval: PropTypes.number.isRequired,
};
